import { PopularItemsRow } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontPopularItems } from '../../../services/portalDashboardService';
import { StorefrontCardCartControl } from './StorefrontItemCard';

/**
 * Popular Items widget: the brand's most widely bought items (anonymous-safe,
 * so logged-out visitors see it too), drawn with the shared row.
 */
export function PopularItemsWidget({
	title,
	limit,
	density,
}: {
	title?: string;
	limit?: number;
	density?: 'standard' | 'compact';
}): JSX.Element | null {
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();
	const displaySettings = useDisplaySettings();

	const queryKey = ['portal', 'dashboard', 'popular-items', limit ?? null];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontPopularItems(token, limit),
		staleTime: 5 * 60_000,
	});

	return (
		<PopularItemsRow
			title={title}
			items={data?.data ?? []}
			isLoading={isLoading}
			isError={isError}
			density={density}
			showPrices={displaySettings.canSeeCatalogPricing}
			onBrowseAll={() => {
				void navigate('/catalog');
			}}
			onSelectItem={(item) => {
				void navigate(`/product/${item.id}`);
			}}
			renderCardActions={(item) => <StorefrontCardCartControl item={item} />}
			onLikelyStaleUrl={() => {
				void queryClient.invalidateQueries({ queryKey });
			}}
		/>
	);
}
